import { get, set, filter, assign, omit, forEach, cloneDeep } from 'lodash';
import { getSetting } from '@utils/helpers';
import { APP_TABS, flavorNameItemTypeTabIdsMap, flavorNameItemTabIdsMap } from './flavorTabs';
import { CElementType, IElementType } from '@ts/ui-models';

export const getInitialValues = () => {
  return {
    isAlive: true,
    currentStatus: { indicators: [] },
  };
};

export const getFlavorInitialValues = flavorName => {
  let values = { isAlive: true };
  switch (flavorName) {
    case 'device':
      values = Object.assign(values, {
        deviceInterfaces: [],
      });
      break;
  }
  return values;
};

export const applyNameRules = (elementType, nameRule) => {
  const { name, logicalId } = elementType;
  const nameRuleMap = {
    'name+uniqueID': `${name}-${logicalId}`,
    uniqueID: logicalId,
    itemName: name,
  };
  return get(nameRuleMap, nameRule, '');
};

const omittedAttrsInFlavors = [
  'id',
  'logicalId',
  'customId',
  'createdAt',
  'updatedAt',
  'aliveStart',
  'aliveEnd',
  'creatorId',
  'updaterId',
  'elementId',
  '_ver',
];
const omittedAttrsInItem = omittedAttrsInFlavors.concat([
  'templateStatusMode',
  'templateStatusId',
  'templateStatus',
  'propagateTemplate',
  'typeParams',
  'isMaster',
  'currentStatus',
  'currentStatusId',
]);
export const buildDefaultValuesFromItemType = (itemType: CElementType | null) => {
  if (!itemType) return {};

  let values: any = omit(itemType, omittedAttrsInItem);
  const { flavors, setItemName } = values;
  if (flavors) {
    forEach(flavors, (value, key) => {
      value = { ...getFlavorInitialValues(key), ...value };
      flavors[key] = omit(value, omittedAttrsInFlavors);
    });
  }

  let currentStatus = getInitialValues().currentStatus;
  const { templateStatus, templateStatusMode } = itemType;
  if (templateStatusMode && templateStatusMode != 'none' && templateStatus) currentStatus = templateStatus;

  assign(values, {
    elementTypeId: itemType.logicalId,
    elementType: {
      id: itemType.id,
      name: itemType.name,
      logicalId: itemType.logicalId,
      customId: itemType.customId,
      mainFlavor: itemType.typeParams?.mainFlavor,
    },
    currentStatus: currentStatus,
    ...(setItemName && { name: applyNameRules(itemType, setItemName) }),
  });
  values.isPreDefaultValue = true;
  return values;
};

export const buildDefaultValuesFromFlavorName = flavorName => {
  let values: any = getInitialValues();
  values.mainFlavor = flavorName;
  values.flavorsLocked = [flavorName];
  let flavorDefaultValues = getFlavorInitialValues(flavorName);
  set(values, `flavors.${flavorName}`, flavorDefaultValues);
  return values;
};

const checkTabExist = (tabs: any[], tabId: string) => !!tabs.find(tab => tab.id === tabId);

const cloneTab = (tabName: string) => {
  return cloneDeep(APP_TABS[tabName]);
};

const getTab = (tabName: string) => {
  const newTab = cloneTab(tabName);
  if (newTab) {
    return newTab;
  }
};

export const isAttrNotNull = (dataPath, attr) => {
  if (!get(dataPath, attr) || get(dataPath, attr) === '') {
    return false;
  }
  return true;
};

export const getFlavorTabs = flavorName => {
  let result: any[] = [];
  result = filter(APP_TABS, { flavor: flavorName });
  return result;
};

const settingsFilterTabs = tabs => {
  if (getSetting('useCustomFields') === false) {
    const idx = tabs.findIndex(tab => tab.id == 'generalCustomFields');
    if (idx !== -1) tabs.splice(idx, 1);
  }
  return tabs;
};

export const addNewFlavorTabs = (kind: 'item' | 'itemType', tabs: any[], flavor: string) => {
  let flavorTabIds = [];
  if (kind === 'item') {
    flavorTabIds = flavorNameItemTabIdsMap[flavor];
  }
  if (kind === 'itemType') {
    flavorTabIds = flavorNameItemTypeTabIdsMap[flavor];
  }
  if (!flavorTabIds) return tabs;

  for (const tabId of flavorTabIds) {
    if (checkTabExist(tabs, tabId)) continue;
    const tab = getTab(tabId);
    if (tab) tabs.push(tab);
  }

  tabs = settingsFilterTabs(tabs);

  if (flavor === 'location') tabs = tabs.filter(t => t.id !== 'locationHistory');
  return tabs;
};
